import React, { useState } from 'react';
import { Plus, Trash2, EyeOff, Eye, Calculator } from 'lucide-react';

const DEFAULT_ROW_LABELS = [
  'Sətir 1: А/A1 | Д/Д1 | И/И1',
  'Sətir 2: Б/Б1 | Е/Е1 | К/К1',
  'Sətir 3: В/В1 | Ж/Ж1 | Л/Л1',
  'Sətir 4: Г/Г1 | З/З1 | М/М1',
];

const DEFAULT_PALETTE = ['#a855f7', '#0ea5e9', '#22c55e', '#eab308', '#ef4444', '#f97316', '#10b981'];

const TableRowsEditor = ({
  rowOrder,
  hiddenRows,
  hiddenColumns,
  customRows,
  onRowOrderChange,
  onHiddenChange,
  onHiddenColumnsChange,
  onCustomChange,
}) => {
  const order = rowOrder || [0, 1, 2, 3];
  const hidden = new Set((hiddenRows || []).map(Number));
  const hiddenCols = new Set((hiddenColumns || []).map(s => String(s).toLowerCase()));
  const rows = customRows || [];
  const [expanded, setExpanded] = useState({});

  const [pendingDelete, setPendingDelete] = useState(null);

  const COLUMN_DEFS = [
    { id: 'yol', label: 'Yol' },
    { id: 'ne', label: 'Nailiyyət Ədədi (NƏ)' },
    { id: 'kd', label: 'Karmik Düyün (KD)' },
  ];

  const toggleColumn = (colId) => {
    const next = new Set(hiddenCols);
    if (next.has(colId)) next.delete(colId); else next.add(colId);
    onHiddenColumnsChange?.(Array.from(next));
  };

  const toggleHidden = (idx) => {
    const next = new Set(hidden);
    if (next.has(idx)) next.delete(idx); else next.add(idx);
    onHiddenChange(Array.from(next));
  };

  const addCustomRow = () => {
    const id = `r_${Date.now().toString(36)}`;
    const newRow = {
      id,
      title: 'Yeni sətir',
      cells: [
        { label: 'Xana 1', formula: 'A+B', color: DEFAULT_PALETTE[rows.length % DEFAULT_PALETTE.length] },
        { label: 'Xana 2', formula: 'A1+B1', color: DEFAULT_PALETTE[(rows.length + 1) % DEFAULT_PALETTE.length] },
        { label: 'Xana 3', formula: 'V', color: DEFAULT_PALETTE[(rows.length + 2) % DEFAULT_PALETTE.length] },
      ],
    };
    onCustomChange([...rows, newRow]);
    setExpanded({ ...expanded, [id]: true });
  };

  const updateCustom = (idx, updated) => {
    const copy = [...rows];
    copy[idx] = updated;
    onCustomChange(copy);
  };

  const performDelete = () => {
    if (pendingDelete === null) return;
    onCustomChange(rows.filter((_, i) => i !== pendingDelete));
    setPendingDelete(null);
  };

  return (
    <div className="admin-section">
      <h3 className="admin-section-title">Cədvəl sətirləri</h3>
      <p className="admin-section-desc">
        Default sətirləri gizlədə bilərsiniz və ya formul ilə yeni sətirlər əlavə edə bilərsiniz.
      </p>

      {/* Column visibility toggles */}
      <div className="mb-6">
        <h4 className="text-sm font-semibold text-white/80 mb-2">Sütunların görünürlüyü</h4>
        <p className="text-xs text-white/40 mb-2">Hər bir sütun qrupunu gizlədə və ya göstərə bilərsiniz.</p>
        <div className="sortable-list">
          {COLUMN_DEFS.map((c) => {
            const isHidden = hiddenCols.has(c.id);
            return (
              <div key={c.id} className="sortable-item" style={{ opacity: isHidden ? 0.5 : 1 }}>
                <div className="sortable-dot" style={{ background: '#a855f7' }} />
                <span className="sortable-label flex-1">{c.label}</span>
                <button
                  type="button"
                  onClick={() => toggleColumn(c.id)}
                  className="admin-btn-secondary"
                  title={isHidden ? 'Göstər' : 'Gizlət'}
                  data-testid={`column-hide-${c.id}`}
                >
                  {isHidden ? <Eye size={14} /> : <EyeOff size={14} />}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Default rows with hide/show */}
      <div className="mb-6">
        <h4 className="text-sm font-semibold text-white/80 mb-2">Standart sətirlər</h4>
        <div className="sortable-list">
          {[0, 1, 2, 3].map((idx) => {
            const isHidden = hidden.has(idx);
            return (
              <div key={idx} className="sortable-item" style={{ opacity: isHidden ? 0.5 : 1 }}>
                <div className="sortable-dot" style={{ background: DEFAULT_PALETTE[idx] }} />
                <span className="sortable-label flex-1">{DEFAULT_ROW_LABELS[idx]}</span>
                <button
                  type="button"
                  onClick={() => toggleHidden(idx)}
                  className="admin-btn-secondary"
                  title={isHidden ? 'Göstər' : 'Gizlət'}
                  data-testid={`row-hide-${idx}`}
                >
                  {isHidden ? <Eye size={14} /> : <EyeOff size={14} />}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Custom rows with formulas */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-semibold text-white/80">Hesablanan sətirlər</h4>
          <button
            type="button"
            onClick={addCustomRow}
            className="admin-btn-primary"
            data-testid="custom-row-add"
          >
            <Plus size={14} /> Sətir əlavə et
          </button>
        </div>
        <p className="text-xs text-white/40 mb-3">
          Formul nümunələri: <code className="text-purple-300">A+B</code>,{' '}
          <code className="text-purple-300">abs(A-V)</code>,{' '}
          <code className="text-purple-300">A1+B1+V1</code>,{' '}
          <code className="text-purple-300">G*2</code>. Açarlar: A, A1, B, B1, V, V1, G, G1, D, D1, E, E1, Zh, Zh1, Z, Z1, I, I1, K, K1, L, L1, M, M1, HRT, HRT1, Man, Man1.
        </p>

        <div className="sortable-list">
          {rows.map((row, ri) => (
            <div key={row.id} className="sortable-item flex-col items-stretch" style={{ alignItems: 'stretch' }}>
              <div className="flex items-center gap-2 w-full">
                <Calculator size={16} className="text-white/50" />
                <input
                  className="karma-input flex-1 text-left"
                  value={row.title}
                  onChange={(e) => updateCustom(ri, { ...row, title: e.target.value })}
                  placeholder="Sətir başlığı"
                  data-testid={`custom-row-title-${row.id}`}
                />
                <button
                  type="button"
                  onClick={() => setExpanded({ ...expanded, [row.id]: !expanded[row.id] })}
                  className="admin-btn-secondary"
                >
                  {expanded[row.id] ? '−' : '+'}
                </button>
                <button
                  type="button"
                  onClick={() => setPendingDelete(ri)}
                  className="admin-btn-secondary"
                  data-testid={`custom-row-delete-${row.id}`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
              {expanded[row.id] && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
                  {(row.cells || []).map((c, ci) => (
                    <div key={ci} className="flex flex-col gap-1 p-2 rounded border border-white/10">
                      <label className="text-xs text-white/50">Xana {ci + 1}</label>
                      <input
                        className="karma-input text-left"
                        value={c.label || ''}
                        placeholder="Adı"
                        onChange={(e) => {
                          const cells = [...row.cells];
                          cells[ci] = { ...c, label: e.target.value };
                          updateCustom(ri, { ...row, cells });
                        }}
                      />
                      <input
                        className="karma-input text-left"
                        value={c.formula || ''}
                        placeholder="Formula (məs: A+B)"
                        onChange={(e) => {
                          const cells = [...row.cells];
                          cells[ci] = { ...c, formula: e.target.value };
                          updateCustom(ri, { ...row, cells });
                        }}
                        data-testid={`custom-row-formula-${row.id}-${ci}`}
                      />
                      <input
                        type="color"
                        value={c.color || '#a855f7'}
                        onChange={(e) => {
                          const cells = [...row.cells];
                          cells[ci] = { ...c, color: e.target.value };
                          updateCustom(ri, { ...row, cells });
                        }}
                        className="w-full h-8 rounded border border-white/10 bg-transparent"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
          {rows.length === 0 && (
            <p className="text-white/40 text-sm">Hesablanan sətir yoxdur.</p>
          )}
        </div>
      </div>

      {pendingDelete !== null && rows[pendingDelete] && (
        <div
          className="mt-4 p-3 rounded border border-red-500/40 bg-red-500/10 flex items-center justify-between gap-3 flex-wrap"
          data-testid="custom-row-delete-confirm-banner"
        >
          <span className="text-sm text-red-200">
            "<strong>{rows[pendingDelete].title || rows[pendingDelete].id}</strong>" sətrini silmək istəyirsiniz?
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              className="admin-btn-secondary"
              onClick={() => setPendingDelete(null)}
              data-testid="custom-row-delete-cancel"
            >
              Ləğv et
            </button>
            <button
              type="button"
              className="admin-btn-primary"
              style={{ background: 'rgba(239,68,68,0.85)', borderColor: '#ef4444' }}
              onClick={performDelete}
              data-testid="custom-row-delete-confirm"
            >
              Sil
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TableRowsEditor;
